/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { DateTime } from "luxon";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import { Activity, Clock, Download, FileText, Globe, LoaderCircle } from "lucide-react";

import { ThemeProps } from "@src/components/Theme";
import DatetimePicker from "@src/components/ui/DatetimePicker";
import {
  ActionButton,
  CardList,
  CardListRow,
  DetailsColumns,
  SectionCard,
} from "@src/components/ui/DetailsKit";
import IconTile from "@src/components/ui/IconTile";
import { T9 } from "@src/components/ui/Lists/ListTable";
import { Close } from "@src/components/ui/TextInput";
import DateUtils from "@src/utils/DateUtils";

import type { Log as LogType } from "@src/@types/Log";
const Wrapper = styled.div<any>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
`;
const InfoUtc = styled.div`
  display: flex;
  gap: 6px;
  font-size: 12px;
  color: ${T9.slate500};
  align-items: center;
  margin-top: 12px;
`;
const Dates = styled.div<any>`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 16px 40px;
  flex-shrink: 0;
`;
const DateWrapper = styled.div<any>`
  position: relative;
`;
const DateLabel = styled.div<any>`
  font-size: 12px;
  color: ${T9.slate500};
  font-weight: ${ThemeProps.fontWeights.medium};
`;
const DateInput = styled.div<any>`
  margin-top: 4px;
  display: flex;
  align-items: center;
`;
const CloseButton = styled(Close)`
  right: -24px;
  top: 29px;
`;
const LogName = styled.div<any>`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  .name {
    font-size: 14px;
    font-weight: 500;
    color: ${T9.slate900};
  }
  .hint {
    font-size: 12px;
    color: ${T9.slate500};
  }
`;
const SmallButton = styled(ActionButton)`
  height: 28px;
  padding: 0 10px;
  font-size: 12px;
`;
// The list grows with the page (one scroll, the workspace's), as on the other Swift pages.
const Scroll = styled.div`
  min-height: 200px;
`;

type State = {
  startDate: Date | null;
  endDate: Date | null;
};
type Props = {
  logs: LogType[];
  onDownloadClick: (
    logName: string,
    startDate?: Date | null,
    endDate?: Date | null,
  ) => void;
  generatingDiagnostics: boolean;
  downloadingAll: boolean;
};
@observer
class DownloadsContent extends React.Component<Props, State> {
  state: State = {
    startDate: null,
    endDate: null,
  };

  handleStartDateChange(startDate: Date) {
    this.setState({ startDate });
  }

  handleEndDateChange(endDate: Date) {
    this.setState({ endDate });
  }

  renderDates() {
    const toUtc = (date: Date) => DateUtils.getUtcDate(date);

    return (
      <Dates>
        <DateWrapper>
          <DateLabel>Start</DateLabel>
          <DateInput>
            <DatetimePicker
              value={this.state.startDate}
              onChange={date => {
                this.handleStartDateChange(date);
              }}
              timezone="utc"
              isValidDate={date => toUtc(date) < DateTime.utc()}
              dispatchChangeContinously
            />
            <CloseButton
              show={this.state.startDate}
              onClick={() => {
                this.setState({ startDate: null });
              }}
            />
          </DateInput>
        </DateWrapper>
        <DateWrapper>
          <DateLabel>End</DateLabel>
          <DateInput>
            <DatetimePicker
              value={this.state.endDate}
              onChange={date => {
                this.handleEndDateChange(date);
              }}
              timezone="utc"
              isValidDate={date => {
                const utcDate = toUtc(date);
                const diffNow = utcDate.diffNow().milliseconds;
                if (this.state.startDate) {
                  // Convert the start date to UTC and subtract one day
                  const startDate = toUtc(this.state.startDate).minus({
                    days: 1,
                  });
                  // Return true if the selected date is before now and after the start date
                  return (
                    diffNow < 0 && utcDate.diff(startDate).milliseconds > 0
                  );
                }
                // If no start date is set, return true if the selected date is before now
                return diffNow < 0;
              }}
              dispatchChangeContinously
            />
            <CloseButton
              show={this.state.endDate}
              onClick={() => {
                this.setState({ endDate: null });
              }}
            />
          </DateInput>
        </DateWrapper>
      </Dates>
    );
  }

  renderLogRow(opts: {
    name: string;
    hint: string;
    icon: typeof FileText;
    busy?: boolean;
    onClick: () => void;
  }) {
    return (
      <CardListRow key={opts.name}>
        <IconTile icon={opts.icon} size={28} />
        <LogName>
          <span className="name">{opts.name}</span>
          <span className="hint">{opts.hint}</span>
        </LogName>
        <SmallButton variant="secondary" disabled={opts.busy} onClick={opts.onClick}>
          {opts.busy ? <LoaderCircle size={14} className="spin" /> : <Download size={14} />}
          {opts.busy ? "Preparing…" : "Download"}
        </SmallButton>
      </CardListRow>
    );
  }

  renderLogs() {
    return (
      <Scroll>
        <CardList>
          {this.renderLogRow({
            name: "diagnostics",
            hint: "A diagnostics bundle of the SwiftIQ services",
            icon: Activity,
            busy: this.props.generatingDiagnostics,
            onClick: () => this.props.onDownloadClick("__diagnostics__"),
          })}
          {this.renderLogRow({
            name: "web-requests",
            hint: "The requests this portal made in your browser",
            icon: Globe,
            onClick: () => this.props.onDownloadClick("__ui__"),
          })}
          {this.props.logs.map(log =>
            this.renderLogRow({
              name: log.log_name,
              hint: "Service log",
              icon: FileText,
              onClick: () =>
                this.props.onDownloadClick(
                  log.log_name,
                  this.state.startDate,
                  this.state.endDate,
                ),
            }),
          )}
        </CardList>
      </Scroll>
    );
  }

  render() {
    return (
      <Wrapper>
        <DetailsColumns columns="minmax(0, 1fr) minmax(0, 1.4fr)">
          <SectionCard
            title="Time range"
            icon={Clock}
            subtitle="Optional: limits the service logs to this period"
          >
            {this.renderDates()}
            <InfoUtc>
              <Clock size={14} />
              Start and end times are in the server&apos;s time zone (UTC).
            </InfoUtc>
            <div style={{ marginTop: 20 }}>
              <ActionButton
                disabled={this.props.downloadingAll}
                onClick={() => {
                  this.props.onDownloadClick(
                    "__all__",
                    this.state.startDate,
                    this.state.endDate,
                  );
                }}
              >
                {this.props.downloadingAll ? (
                  <LoaderCircle size={16} className="spin" />
                ) : (
                  <Download size={16} />
                )}
                {this.props.downloadingAll ? "Preparing the logs…" : "Download all logs"}
              </ActionButton>
            </div>
          </SectionCard>
          <SectionCard title="Logs" icon={FileText} subtitle="Download one log at a time">
            {this.renderLogs()}
          </SectionCard>
        </DetailsColumns>
      </Wrapper>
    );
  }
}

export default DownloadsContent;
